import { useContext, useEffect, useState } from 'react'
import { Button, Container, Grid, Typography } from "@mui/material"
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGiftRepository } from '../../../../repositories/GiftSubscription/use-gift-repository'
import { Route } from '../../../../router'
import { AppContext } from '../../../../stores/App/AppStore'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import MarkdownContent from '../../../atoms/MarkdownContent/MarkdownContent'
import Spacer from '../../../atoms/Spacer/Spacer'
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"
import useGoogleTagManager from '../../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from '../../../../helpers/analytics/GtmEventName'
import GiftRepeatableContent from "../../../molecules/GiftRepeatableContent/GiftRepeatableContent"
import BannerImage from '../../../atoms/BannerImage/BannerImage'
import { useMembershipPlanRepository } from '../../../../repositories/MembershipPlan/use-membership-plan-repository'
import { UserContext } from '../../../../stores/User/UserStore'
import { autoPlay } from 'react-swipeable-views-utils'
import SwipeableViews from 'react-swipeable-views'
import theme from '../../../../themes/starter'
import styles from './GiftSubscriptionLanding.module.css'

const GiftSubscriptionLanding = () => {
    useTrackPageView(`Gift Subscription Landing Page`)

    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const plan = searchParams.get(`plan`)
    const navigate = useNavigate()
    const { trackButtonClick } = useGoogleTagManager()
    const { saveMembershipPlanKey, retrieveMembershipPlan } = useMembershipPlanRepository()
    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)
    const { retrieveGiftSubscriptionLandingPage } = useGiftRepository()
    const [activeStep, setActiveStep] = useState(0)

    const handleStepChange = (step: number) => {
        setActiveStep(step)
    }

    const membershipPlanKey = plan?.toUpperCase()

    const membershipPlan = userState.membershipPlan?.data

    useEffect(() => {
        if (!appState.pages.giftSubscriptionLandingPage.bannerImageURL.length){
            retrieveGiftSubscriptionLandingPage()
        }
        if (membershipPlanKey) {
            saveMembershipPlanKey(membershipPlanKey)
            retrieveMembershipPlan(membershipPlanKey)
        }
    }, [])

    const handleNextButton = () => {
        if (membershipPlan){
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                membershipPlanKey: membershipPlanKey,
            }
        }
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_LANDING_PAGE_SUBMIT)
        navigate(Route.SUBSCRIPTION_GIFT_DECLARATION)
    }

    const handleGiftStatusButtonClick = () => {
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_CHECK_GIFT_STATUS_CLICK)
        navigate(Route.SUBSCRIPTION_GIFT_CHECK_STATUS)
    }

    const AutoPlaySwipeableViews = autoPlay(SwipeableViews)
    const autoPlayAxis = theme.direction === `rtl` ? `x-reverse` : `x`

    return (
        <Container>
            <CommonBackButton path={Route.HOME} />
            <AutoPlaySwipeableViews
                axis={autoPlayAxis}
                index={activeStep}
                onChangeIndex={handleStepChange}
                enableMouseEvents
            >
                {appState.pages.giftSubscriptionLandingPage.bannerImageURL.map((item) => {
                    return <BannerImage imageUrl={item} className={styles.bannerImage} alt="logo" key={item}/>
                })}
            </AutoPlaySwipeableViews>
            <Typography variant={`h3`}>
                {appState.pages.giftSubscriptionLandingPage.title}
            </Typography>
            <MarkdownContent content={appState.pages.giftSubscriptionLandingPage.description} />
            <Spacer />
            <GiftRepeatableContent repeatableContent={appState.pages.giftSubscriptionLandingPage.repeatableContent ?? []}></GiftRepeatableContent>
            <Spacer />
            <Grid>
                <Button variant='contained' onClick={handleNextButton} fullWidth>{t(`gift_subscription_landing_next`)}</Button>
            </Grid>
            <Spacer height={15}/>
            <Grid>
                <Button variant='outlined' onClick={handleGiftStatusButtonClick} fullWidth>{t(`gift_subscription_landing_status`)}</Button>
            </Grid>
        </Container>
    )
}

export default GiftSubscriptionLanding